import React, { useState, useEffect, useRef } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Legend, ComposedChart
} from 'recharts';
import { AURAVEX_LOGO } from './logo';
import { INR, PLANS, Badge, Dot, KPI, Panel, Prog, Pill, BtnStyles, Btn, Inp, Sel, Textarea, Modal, Toast, DataTable, PageHeader, AlertBox, Grid, Divider, CameraLiveModal } from './utils';
import { PageDashboard, PageTenants, PageCameras } from './pages_cam';
import { FIRModal, PageEvents, PageModels, PageTraffic, PageRules } from './pages_ev';
import { PageUsers, PageSubscriptions, PageCO2, PageReports } from './pages_usr';
import { PageInfra, PageAudit, PageNotifications, PageAIAgent } from './pages_admin';
import { PageLiveStream, PageIncidentLog, DEFAULT_PAGE_ACCESS } from './pages_stream';
import { Sidebar, TopBar, FloatingAIAvatar, PageAccessControl } from './pages_nav';
import SignupWizard from './SignupWizard';
import { PageTraining } from './pages_training';
import { PageNotifyRecipients } from './pages_notify';
import { PageMyModelRequests, PageModelRequests } from './pages_modelreq';
import { PagePlatformConfig } from './pages_config';
import { PageAnnotate } from './pages_annotate';
import AICommandPage from './pages_aicommand';
import SafetyPage from './pages_safety';
import { PageAISuite } from './pages_ai_suite';
import { PageAgentOps } from './pages_agent_ops';
import MqttAgentControlPage from "./pages_mqtt";
import { PageMarketing } from './pages_marketing';
import { PageSales } from './pages_sales';
import FaceDBPage from './pages_facedb';
import LoginPage from './LoginPage';

// ============================================================
// LIVE API INTEGRATION - AURAVEX Backend
// ============================================================

const API_NODE   = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL : '';
const API_PYTHON = (typeof process !== 'undefined' && process.env?.REACT_APP_PYTHON_URL)
  ? process.env.REACT_APP_PYTHON_URL : 'http://localhost:8000';
const WS_URL_BASE = (typeof process !== 'undefined' && process.env?.REACT_APP_WS_URL)
  ? process.env.REACT_APP_WS_URL : 'ws://localhost:5000/ws';

const apiFetch = async (path, opts = {}, base = API_NODE) => {
  try {
    const token = localStorage.getItem('auravex_token');
    const res = await fetch(base + path, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...opts.headers,
      },
      ...opts,
    });
    if (res.status === 401) {
      localStorage.removeItem('auravex_token');
      localStorage.removeItem('auravex_user');
      window.dispatchEvent(new CustomEvent('auravex:401'));
      return null;
    }
    if (!res.ok) {
      let errMsg = `HTTP ${res.status}`;
      try { const j = await res.json(); errMsg = j.error || j.message || errMsg; } catch {}
      throw new Error(errMsg);
    }
    return await res.json();
  } catch (e) {
    console.warn(`[API] ${opts.method||'GET'} ${path}:`, e.message);
    throw e;  // re-throw so callers can handle it
  }
};
const pyFetch   = (p, o={}) => apiFetch(p, o, API_PYTHON);
const apiPost   = (p, d) => apiFetch(p, { method: 'POST',   body: JSON.stringify(d) });
const apiPut    = (p, d) => apiFetch(p, { method: 'PUT',    body: JSON.stringify(d) });
const apiPatch  = (p, d) => apiFetch(p, { method: 'PATCH',  body: JSON.stringify(d) });
const apiDelete = (p)    => apiFetch(p, { method: 'DELETE' });

// Map DB snake_case -> frontend camelCase
const mapTenant = r => r ? ({
  ...r,
  plan:    r.plan_id   ?? r.plan   ?? 'starter',
  maxCam:  r.max_cameras ?? r.max_cam ?? r.maxCam ?? 0,
  maxU:    r.max_users   ?? r.max_u   ?? r.maxU   ?? 0,
}) : null;
const mapCamera = r => r ? ({
  ...r,
  siteId:  r.site_id    ?? r.siteId  ?? '',
  res:     r.resolution ?? r.res     ?? '1080p',
  models:  Array.isArray(r.models) ? r.models : (typeof r.models==='string' ? JSON.parse(r.models||'[]') : []),
}) : null;
const mapEvent  = r => r ? ({
  ...r,
  caseId:  r.case_id   ?? r.caseId  ?? '',
  tId:     r.tenant_id ?? r.t_id    ?? r.tId    ?? '',
  siteId:  r.site_id   ?? r.siteId  ?? '',
  camId:   r.cam_id    ?? r.camId   ?? '',
  sev:     r.severity  ?? r.sev     ?? 'Medium',
  evidence: Array.isArray(r.evidence) ? r.evidence : [],
}) : null;
const mapUser   = r => r ? ({
  id:        r.id,
  name:      r.name,
  email:     r.email,
  role:      r.role,
  tenantId:  r.tenant_id   ?? r.tenantId ?? '',
  status:    r.status      ?? 'Active',
  mfa:       r.mfa_enabled ?? r.mfa      ?? false,
  last:      r.last_login  ?? r.last_seen ?? r.last ?? '',
  mobiles:   Array.isArray(r.mobiles)  ? r.mobiles  : (typeof r.mobiles ==='string' ? JSON.parse(r.mobiles  ||'[]') : []),
  emails:    Array.isArray(r.emails)   ? r.emails   : (typeof r.emails  ==='string' ? JSON.parse(r.emails   ||'[]') : [r.email||'']),
  notifyVia: (typeof r.notify_via==='object' && r.notify_via!==null) ? r.notify_via : (typeof r.notify_via==='string' ? JSON.parse(r.notify_via||'{}') : r.notifyVia || {email:true}),
}) : null;
const mapJetson = r => r ? ({ ...r, siteId: r.site_id??r.siteId, tId: r.tenant_id??r.t_id??r.tId }) : null;
const mapViol   = r => r ? ({ ...r, camId: r.cam_id??r.camId }) : null;
/* -- API payload converters (Frontend → Backend field names) -- */
const tenantToAPI  = t => ({ name:t.name, sector:t.sector, plan_id:t.plan||t.plan_id||'starter', contact:t.contact||'', billing:t.billing||'monthly', renewal:t.renewal||null, status:t.status, onboarded:t.onboarded||false });
const cameraToAPI  = c => ({ site_id:c.siteId||c.site_id, name:c.name, brand:c.brand||'Hikvision', type:c.type||'Fixed', ip:c.ip||'', resolution:c.res||c.resolution||'1080p', fps:c.fps||25, models:c.models||[], retention:c.retention||'30d', zone:c.zone||'', status:c.status, health:c.health });
const userToAPI    = u => ({ name:u.name, email:u.email||(u.emails&&u.emails[0])||'', role:u.role, tenant_id:u.tenantId||u.tenant_id||null, mobiles:u.mobiles||[], emails_list:u.emails||[], notify_via:u.notifyVia||u.notify_via||{email:true}, mfa_enabled:u.mfa||false, status:u.status });
const ruleToAPI    = r => ({ name:r.name, trigger:r.trigger||'', condition:r.condition||'', action:r.action||'', tenant_id:r.tId||r.tenant_id||'All', status:r.status||'Active' });

/* -- SECTOR AVATARS ------------------------------------------- */
const AVATAR_GOV  = "data:image/jpeg;base64,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";   // Male  - Government
const AVATAR_PVT  = "data:image/jpeg;base64,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"; // Female - Private / Industrial
const sectorAvatar = (sector) =>
  (sector==="Government"||sector==="Municipal"||sector==="Defence"||sector==="Police")
    ? AVATAR_GOV : AVATAR_PVT;

/* -- DESIGN TOKENS -- CLEAN WHITE LIGHT THEME ------------------- */
const T = {
  /* -- Surface hierarchy -- */
  bg:"#F0F2F7",     /* Page background -- very light grey-blue */
  deep:"#FFFFFF",   /* Sidebar background */
  panel:"#FFFFFF",  /* Card panels */
  card:"#FFFFFF",   /* Inline cards */
  border:"#E2E8F3", /* Subtle borders */
  hi:"#EEF2FB",     /* Hover highlight */
  glow:"#DDE5F6",   /* Active glow */
  faint:"#F7F9FD",  /* Input backgrounds */
  /* Gold primary -- AURAVEX brand */
  accent:"#C48A0E", accentD:"#9A6B00", accentLo:"#C48A0E18",
  /* Electric blue */
  blue:"#1A5FB4",   blueLo:"#1A5FB414",
  /* Green */
  green:"#1E8A55",  greenLo:"#1E8A5514",
  /* Standard semantic */
  red:"#C0392B",    redLo:"#C0392B12",
  purple:"#6527BE", purpleLo:"#6527BE12",
  teal:"#0E7490",   tealLo:"#0E749012",
  orange:"#C05800", orangeLo:"#C0580012",
  pink:"#A21060",   pinkLo:"#A2106012",
  /* Text -- dark on white */
  text:"#1A2235",   muted:"#7A90B0",
  /* Silver */
  silver:"#4A607A",
  font:"'DM Sans',system-ui,sans-serif",
  mono:"'IBM Plex Mono','Fira Mono',monospace",
};

const sc  = s => ({Online:T.green,Active:T.green,Stable:T.green,Resolved:T.green,
  Offline:T.red,Suspended:T.red,Locked:T.red,Critical:T.red,
  Degraded:T.accent,Acknowledged:T.accent,High:T.accent,
  Medium:T.orange,Beta:T.orange,Inactive:T.muted,
  Low:T.muted,Info:T.blue,Trial:T.purple,Open:T.red,
  "Challan Issued":T.blue,"Challan Pending":T.orange,
  "Court Referral":T.purple,Disputed:T.pink}[s]||T.muted);

/* -- PLANS (INR) -------------------------------------------------- */

/* -- TENANTS ------------------------------------------------------ */
const TENANTS_INIT = []; // Live data from API — add tenants via UI

/* -- SITES -------------------------------------------------------- */
const SITES_DATA = []; // Live data from API — populated via /api/cameras/sites/all

/* -- CAMERAS ------------------------------------------------------ */
const CAMERAS_INIT = []; // Live data from API

/* -- EVENTS ------------------------------------------------------- */
const EVENTS_INIT = []; // Live data from API

/* -- VIOLATIONS (TRAFFIC) ----------------------------------------- */
const VIOLS_INIT = []; // Live data from API

/* -- MODELS ------------------------------------------------------- */
const MODELS_INIT = []; // Loaded from API (/api/models) — seeded from schema.sql

/* -- RULES -------------------------------------------------------- */
const RULES_INIT = []; // Live data from API

/* -- USERS -------------------------------------------------------- */
const USERS_INIT = []; // Live data from API

/* -- JETSON NODES ------------------------------------------------- */
const JETSONS_INIT = []; // Live data from API

/* -- AUDIT LOG ---------------------------------------------------- */
const AUDIT_INIT = []; // Live data from API

/* -- REPORT TEMPLATES --------------------------------------------- */
const REPORTS_INIT = []; // Live data from API

/* -- CHART DATA --------------------------------------------------- */
const alertHours = Array.from({length:12},(_,i)=>({
  h:`${String(i*2).padStart(2,"0")}:00`,
  critical:Math.floor(Math.random()*4)+1,
  high:Math.floor(Math.random()*7)+2,
  medium:Math.floor(Math.random()*10)+3,
}));
const trafficHours = Array.from({length:12},(_,i)=>({
  h:`${String(i*2).padStart(2,"0")}:00`,
  violations:Math.floor(Math.random()*18)+2,
  vehicles:Math.floor(Math.random()*900)+200,
}));
const co2Data = [
  {m:"Jan",co2:12.4,fuel:520,saved:28000,idle:310},
  {m:"Feb",co2:11.8,fuel:492,saved:31000,idle:285},
  {m:"Mar",co2:10.9,fuel:455,saved:35000,idle:260},
  {m:"Apr",co2:10.1,fuel:421,saved:38500,idle:238},
  {m:"May",co2:9.4, fuel:392,saved:42000,idle:212},
  {m:"Jun",co2:8.6, fuel:358,saved:47000,idle:190},
];
const perfWeeks = [
  {w:"W1",ppe:95.5,anpr:97.8,fire:96.2,crowd:91.0},
  {w:"W2",ppe:96.2,anpr:98.1,fire:96.9,crowd:92.1},
  {w:"W3",ppe:97.1,anpr:98.6,fire:97.8,crowd:93.4},
  {w:"W4",ppe:97.8,anpr:99.1,fire:98.5,crowd:94.1},
];
const radarData = [
  {s:"Compliance",v:94},{s:"Response",v:88},{s:"Coverage",v:92},
  {s:"Uptime",v:99},{s:"Accuracy",v:97},{s:"Cost Eff.",v:83},
];

/* -- AI AGENT RESPONSES ------------------------------------------- */
const AI_RESPONSES = {
  "last week":    "Last week (Jun 24-30), the platform detected <b>1,847 events</b> across all tenants.<br>🔴 <b>Critical:</b> Tata Motors CAM-006 (Welding Bay) -- 14 PPE violations in 5 days. Trend worsening -- immediate safety audit recommended.<br>🟡 <b>Shivajinagar Junction</b> -- 312 signal violations, 22% above weekly average.<br>🔒 <b>MSRTC Depot</b> auto-suspended -- storage quota breach (92%).<br>✅ <b>Uptime:</b> 99.4% platform-wide.",
  "predict":      "⚠️ <b>Next 7-Day Risk Forecast (AI Analysis):</b><br><b>1. J-004 Edge Node</b> -> GPU at 94%, RAM at 94% -- thermal degradation expected within 36-48 hours. <i>Action: Reboot + thermal paste replacement</i>.<br><b>2. Monsoon forecast Pune</b> (Jul 5-12) -> Historical data shows +38% accident spike. <i>Action: Enable fog-mode ANPR, raise alert sensitivity on all traffic cameras</i>.<br><b>3. Infosys storage at 88%</b> -> Quota breach in ~12 days. <i>Action: Upgrade to 2 TB plan</i>.",
  "violation":    "🔴 <b>Top 5 Violation Hotspots -- Last 30 Days:</b><br>1. Shivajinagar Junction (CAM-001/002) -- 1,247 violations - 61% signal jumps<br>2. FC Road Stretch (CAM-003) -- 892 violations - 74% overspeeding<br>3. Kothrud Junction -- 614 violations - wrong way + no helmet<br>4. Baner Road -- 521 violations - triple riding<br>5. Warje Bridge -- 398 violations - overspeeding<br><br>💰 Total fines generated: <b>₹14.7 Lakh</b> this month.",
  "ppe":          "📊 <b>PPE Compliance -- Tata Motors (30-day trend):</b><br>Current rate: <b>73.2%</b> v from 81.4% in May.<br>* CAM-005 (Assembly Line A) = 68% of all violations<br>* Peak violation window: <b>7-9 AM</b> (shift change)<br>* CAM-006 degraded = underreporting estimated at ~20%<br>* Forklift zone violations rising +14% this week<br><b>Recommendation:</b> Repair CAM-006 urgently. Schedule mandatory PPE briefing for morning shift. Add zone-entry interlock.",
  "monsoon":      "🌧️ <b>Monsoon Preparedness Plan (Jul-Sep 2025):</b><br>1. Enable weather-adaptive PTZ on all outdoor cameras<br>2. Raise ANPR confidence threshold to 94% to reduce rain false-positives<br>3. Pre-position maintenance team at J-001, J-002 (waterproofing + cable sealing)<br>4. Escalate medium->high sensitivity at known flood-prone intersections<br>5. Schedule daily camera health checks for outdoor units<br>6. Historical: Jun-Aug = +38% incident spike -- send advisory to all tenant admins",
  "cost":         "💰 <b>Platform ROI Summary -- Year To Date:</b><br>* Fuel savings: <b>₹2.84 Lakh</b> across all monitored fleets<br>* CO2 avoided: <b>51.8 tonnes</b> (~ 860 trees planted equivalent)<br>* Traffic fine revenue generated: <b>₹14.7 Lakh</b> (Pune Smart City)<br>* Incident prevention value (insurance est.): <b>₹8.2 Lakh</b><br>* Platform subscription MRR: <b>₹1.58 Lakh</b><br>--------------------------<br>🏆 <b>Average tenant ROI: 3.2x investment</b>",
};
const CHAT_INIT = [{role:"ai",msg:"Namaste! 🙏 I'm <b>AURAVEX GLOBAL AI Copilot</b> -- your AI analyst with 30 days of platform context.<br>I can help with: incident analysis, predictive risk forecasts, compliance trends, cost savings, traffic patterns, and preventive action plans.<br>Type your question or pick a quick prompt ->",time:"now"}];
/* ================================================================
   ATOMIC COMPONENTS
================================================================ */

/* Badge */

export default function App() {
  /* -- STATE ---------------------------------------------------- */
  const [loggedIn,    setLoggedIn]    = useState(() => {
    try {
      const user = localStorage.getItem('auravex_user');
      return !!user;
    } catch { return false; }
  });
  const [loginUser,   setLoginUser]   = useState(() => {
    try {
      const u = localStorage.getItem('auravex_user');
      return u ? JSON.parse(u) : {name:"Super Admin",role:"superadmin"};
    } catch { return {name:"Super Admin",role:"superadmin"}; }
  });

  const handleLogin = (name, role, userObj) => {
    const mappedRole = role==="superadmin"?"superadmin":role==="government"?"government":"private";
    // Auto-set role for new users
    if(!localStorage.getItem("auravex_role")&&user&&user.email){
      const _isGov=user.tenantId&&user.tenantId.startsWith("T-GOV");
      const _autoRole=user.role==="Super Admin"?"superadmin":_isGov?"government":"private";
      localStorage.setItem("auravex_role",_autoRole);
      window.location.reload();
    }
    const u = {name, role:mappedRole, id:userObj?.id, email:userObj?.email, tenantId:userObj?.tenantId||userObj?.tenant_id};
    setLoginUser(u);
    setCurrentRole(mappedRole);
    localStorage.setItem('auravex_user', JSON.stringify(u));
    setLoggedIn(true);
  };
  const handleLogout = () => {
    localStorage.removeItem("auravex_token");
    localStorage.removeItem("auravex_user");
    setLoggedIn(false);
    setLoginUser({name:"",role:"superadmin"});
    setCurrentRole("superadmin");
    setActive("dashboard");
    // Clear all data state on logout for security
    setTenants([]); setCameras([]); setEvents([]); setViolations([]);
    setUsers([]); setJetsons([]); setReports([]); setAudit([]);
    setModels([]); setRules([]); setSites([]);
  };
  const [active,      setActive]      = useState("dashboard");
  const [currentRole, setCurrentRole] = useState("superadmin");
  const [pageAccess,  setPageAccess]  = useState({
    government: [...DEFAULT_PAGE_ACCESS.government],
    private:    [...DEFAULT_PAGE_ACCESS.private],
  });
  const [plans,   setPlans]    = useState(PLANS.map(p=>({...p})));
  const [tenants, setTenants]  = useState(TENANTS_INIT);
  const [cameras, setCameras]  = useState(CAMERAS_INIT);
  const [events,  setEvents]   = useState(EVENTS_INIT);
  const [violations, setViolations] = useState(VIOLS_INIT);
  const [models,  setModels]   = useState(MODELS_INIT);
  const [rules,   setRules]    = useState(RULES_INIT);
  const [users,   setUsers]    = useState(USERS_INIT);
  const [jetsons, setJetsons]  = useState(JETSONS_INIT);
  const [sites,   setSites]    = useState([]);
  const [reports, setReports]  = useState(REPORTS_INIT);
  const [audit,   setAudit]    = useState(AUDIT_INIT);
  const [toast,   setToast]    = useState(null);
  const [time,    setTime]     = useState("");
  const [notifLog,setNotifLog] = useState([]);

  /* -- RESTORE ROLE FROM SAVED SESSION ----------------------- */
  useEffect(()=>{
    try {
      const u = localStorage.getItem('auravex_user');
      if(u) {
        const parsed = JSON.parse(u);
        if(parsed.role){ var _mr=parsed.role==="superadmin"||parsed.role==="Super Admin"?"superadmin":parsed.role==="government"?"government":"private"; setCurrentRole(_mr); }
      }
    } catch {}
    // Listen for 401 from any API call → force logout
    const on401 = () => handleLogout();
    window.addEventListener('auravex:401', on401);
    return () => window.removeEventListener('auravex:401', on401);
  // eslint-disable-next-line
  },[]);

  /* -- ROLE-GUARD: redirect if current page forbidden ----------- */
  useEffect(()=>{
    if(currentRole==="superadmin") return;
    const allowed = pageAccess[currentRole]||[];
    if(!allowed.includes(active)){
      setActive("dashboard");
    }
  },[currentRole, pageAccess, active]);

  /* -- LIVE API DATA LOADER ------------------------------------ */
  useEffect(()=>{
    const loadFromAPI = async () => {
      try {
        const [t, c, e, v, u, j, si, r, a, m, rl] = await Promise.all([ // order matches the request list below
          apiFetch('/api/tenants'),
          apiFetch('/api/cameras'),
          apiFetch('/api/events?limit=200'),
          apiFetch('/api/violations'),
          apiFetch('/api/users'),
          apiFetch('/api/jetsons'),
          apiFetch('/api/cameras/sites/all'),
          apiFetch('/api/reports'),
          apiFetch('/api/audit'),
          apiFetch('/api/models'),
          apiFetch('/api/rules'),
        ]);
        const eArr = Array.isArray(e) ? e : (e?.data || []);
        const mapped = {
          tenants:  (Array.isArray(t)  ? t  : []).map(mapTenant).filter(Boolean),
          cameras:  (Array.isArray(c)  ? c  : []).map(mapCamera).filter(Boolean),
          events:   eArr.map(mapEvent).filter(Boolean),
          violations: Array.isArray(v) ? v : [],
          users:    (Array.isArray(u)  ? u  : []).map(mapUser).filter(Boolean),
          jetsons:  (Array.isArray(j)  ? j  : []).map(mapJetson).filter(Boolean),
          reports:  Array.isArray(r)   ? r  : [],
          audit:    (Array.isArray(a)  ? a  : []).map(al=>({...al, sev:al.severity||al.sev||'Info', time:al.created_at||al.time})),
          models:   (Array.isArray(m)  ? m  : []).map(am=>({...am, acc:parseFloat(am.accuracy||am.acc||0), lat:am.latency||am.lat, sz:am.size||am.sz, st:am.status||am.st||'Active', dep:parseInt(am.deployments||am.dep||0), cat:am.category||am.cat, ver:am.version||am.ver})),
          rules:    (Array.isArray(rl) ? rl : []).map(rule=>({...rule, tId:rule.tenant_id||rule.tId||'All'})),
        };
        // Only replace state if we actually got data (prevents blank screen if one endpoint fails)
        if(mapped.tenants.length)    setTenants(mapped.tenants);
        if(mapped.cameras.length)    setCameras(mapped.cameras);
        if(mapped.events.length)     setEvents(mapped.events);
        if(mapped.violations.length) setViolations(mapped.violations);
        if(mapped.users.length)      setUsers(mapped.users);
        if(mapped.jetsons.length)    setJetsons(mapped.jetsons);
        if(mapped.reports.length)    setReports(mapped.reports);
        if(mapped.audit.length)      setAudit(mapped.audit);
        if(mapped.models.length)     setModels(mapped.models);
        if(mapped.rules.length)      setRules(mapped.rules);
        if(Array.isArray(si) && si.length) setSites(si);
        console.log('[AURAVEX] Live data loaded from API ✅', Object.fromEntries(Object.entries(mapped).map(([k,v])=>[k,v.length])));
      } catch(err) {
        console.warn('[AURAVEX] API unavailable, using demo data:', err.message);
      }
    };
    loadFromAPI();
    // Refresh data every 30 seconds for multi-user sync
    const refreshTimer = setInterval(loadFromAPI, 30000);
    return () => clearInterval(refreshTimer);
  },[]);

  /* -- WEBSOCKET LIVE EVENTS ------------------------------------ */
  useEffect(()=>{
    let ws;
    const connect = () => {
      try {
        ws = new WebSocket(WS_URL_BASE);
        ws.onopen = () => console.log('[WS] Connected to AURAVEX live feed');
        ws.onmessage = (e) => {
          try {
            const { event, data } = JSON.parse(e.data);
            if(event==='event:created')   setEvents(p=>[mapEvent(data),...p].slice(0,500));
            if(event==='tenant:created')  setTenants(p=>[...p, mapTenant(data)]);
            if(event==='tenant:updated')  setTenants(p=>p.map(t=>t.id===data.id?mapTenant(data):t));
            if(event==='tenant:deleted')  setTenants(p=>p.filter(t=>t.id!==data.id));
            if(event==='camera:created')  setCameras(p=>[...p, mapCamera(data)]);
            if(event==='camera:updated')  setCameras(p=>p.map(c=>c.id===data.id?mapCamera(data):c));
            if(event==='camera:deleted')  setCameras(p=>p.filter(c=>c.id!==data.id));
            if(event==='user:created')    setUsers(p=>[...p, mapUser(data)]);
            if(event==='user:updated')    setUsers(p=>p.map(u=>u.id===data.id?mapUser(data):u));
            if(event==='user:deleted')    setUsers(p=>p.filter(u=>u.id!==data.id));
            if(event==='audit:created')   setAudit(p=>[data,...p].slice(0,1000));
          } catch {}
        };
        ws.onerror  = () => {};
        ws.onclose  = () => setTimeout(connect, 5000); // auto-reconnect
      } catch {}
    };
    connect();
    return () => { if(ws) ws.close(); };
  },[]);

  /* -- CLOCK ---------------------------------------------------- */
  useEffect(()=>{
    const tick=()=>setTime(new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",second:"2-digit"}));
    tick(); const t=setInterval(tick,1000); return()=>clearInterval(t);
  },[]);

  /* -- TOAST HELPER --------------------------------------------- */
  const showToast = (msg,type="success") => {
    setToast({msg,type});
    setTimeout(()=>setToast(null),3000);
  };

  /* -- NOTIFICATION SENDER (SMS + Email simulation) -------------- */
  const sendNotification = (to, subject, body, channels=["email"], user=null) => {
    const ts = new Date().toLocaleTimeString("en-IN");
    const now = Date.now();
    // Determine actual channels -- respect user.notifyVia preferences
    let effectiveChannels = user?.notifyVia
      ? channels.filter(ch =>
          ch==="email"    ? user.notifyVia.email     :
          ch==="sms"      ? user.notifyVia.sms       :
          ch==="whatsapp" ? user.notifyVia.whatsapp  : true
        )
      : [...channels];
    if(effectiveChannels.length===0) effectiveChannels = ["email"]; // fallback
    const channelTypeMap = {email:"Email", sms:"SMS", whatsapp:"WhatsApp", webhook:"Webhook", slack:"Slack"};
    const entries = effectiveChannels.map((ch,ci)=>({
      id:`N${String(now+ci).slice(-6)}`,
      time:ts,
      type:channelTypeMap[ch]||"Email",
      // For SMS/WhatsApp use first mobile; for email use first email address
      to: (ch==="whatsapp"||ch==="sms")
          ? (user?.mobiles?.filter(Boolean)?.[0] || user?.mobile || to)
          : (user?.emails?.filter(Boolean)?.[0] || user?.email || to),
      subject,
      msg:body,
      status:"Delivered",
      sev:"Info",
    }));
    setNotifLog(p=>[...entries,...p].slice(0,200));
    // Sync to Python FastAPI notification service (fire and forget)
    entries.forEach(entry => {
      const channel = entry.type?.toLowerCase() || 'email';
      const isPhoneChannel = channel === 'sms' || channel === 'whatsapp';
      pyFetch('/api/notify', {
        method: 'POST',
        body: JSON.stringify({
          recipient_mobile: isPhoneChannel ? entry.to : null,
          recipient_email:  !isPhoneChannel ? entry.to : null,
          subject: entry.subject,
          body: entry.msg,
          channel,
          tenant_id: null,
          event_id: null,
        })
      }).catch(()=>{});
    });
    const icons = {email:"📧", sms:"💬", whatsapp:"📱", webhook:"🔗", slack:"💼"};
    const chIcons = effectiveChannels.map(c=>icons[c]||"📤").join(" ");
    showToast(`${chIcons} Sent to ${user?.name||to} via ${effectiveChannels.join(", ")}`, "success");
  };
  
  /* -- SEND CUSTOM NOTIFICATION ----------------------------------- */
  const sendCustomNotification = (userObj, subject, body) => {
    const chs = [];
    if(userObj.notifyVia?.email)    chs.push("email");
    if(userObj.notifyVia?.sms)      chs.push("sms");
    if(userObj.notifyVia?.whatsapp) chs.push("whatsapp");
    sendNotification(userObj.email, subject, body, chs.length?chs:["email"], userObj);
  };

  /* -- AUDIT LOGGER ---------------------------------------------- */
  const logAudit = (action, target, sev="Info") => {
    const ts = new Date().toLocaleTimeString("en-IN");
    const entry = {
      id:`AU-${String(Date.now()).slice(-6)}`,
      time:ts, actor:"Super Admin", action, target,
      ip:"192.168.1.1", sev
    };
    setAudit(p=>[entry,...p].slice(0,100));
    // Sync to backend (fire and forget)
    apiPost('/api/audit', {actor:entry.actor||'Super Admin', action:entry.action, target:entry.target||'', ip:entry.ip||'system', severity:entry.sev||'Info'}).catch(()=>{});
  };

  /* -- ROUTE -> COMPONENT ---------------------------------------- */
  const renderPage = () => {
    const props = {toast:showToast, sendNotification, logAudit};
    switch(active){
      case "dashboard":     return <PageDashboard  events={events} tenants={tenants} cameras={cameras} violations={violations} plans={plans}/>;
      case "livestream":    return <PageLiveStream  cameras={cameras} events={events} setEvents={setEvents} {...props}/>;
      case "incidentlog":   return <PageIncidentLog events={events} setEvents={setEvents} {...props}/>;
      case "tenants":       return <PageTenants    tenants={tenants} setTenants={setTenants} plans={plans} {...props}/>;
      case "subscriptions": return <PageSubscriptions tenants={tenants} setTenants={setTenants} plans={plans} setPlans={setPlans} {...props}/>;
      case "users":         return <PageUsers      users={users} setUsers={setUsers} tenants={tenants} {...props}/>;
      case "cameras":       return <PageCameras    cameras={cameras} setCameras={setCameras} tenants={tenants} sites={sites} jetsons={jetsons} models={models} {...props}/>;
      case "infrastructure":return <PageInfra      jetsons={jetsons} setJetsons={setJetsons} {...props}/>;
      case "models":        return <PageModels     models={models} setModels={setModels} tenants={tenants} {...props}/>;
      case "rules":         return <PageRules      rules={rules} setRules={setRules} tenants={tenants} {...props}/>;
      case "events":        return <PageEvents     events={events} setEvents={setEvents} {...props}/>;
      case "traffic":       return <PageTraffic    violations={violations} {...props}/>;
      case "notifications": return <PageNotifications notifLog={notifLog} sendNotification={sendNotification} viewOnly={currentRole!=="superadmin"} {...props}/>;
      case "reports":       return <PageReports    reports={reports} setReports={setReports} tenants={tenants} {...props}/>;
      case "co2":           return <PageCO2 {...props}/>;
      case "aiagent":       return <PageAIAgent/>;
      case "audit":         return <PageAudit      audit={audit} {...props}/>;
      case "accesscontrol": return <PageAccessControl pageAccess={pageAccess} setPageAccess={setPageAccess} currentRole={currentRole} setCurrentRole={setCurrentRole} {...props}/>;
      case "facedb":        return <FaceDBPage cameras={cameras} {...props}/>;
      case "sales":         return <PageSales tenants={tenants} {...props}/>;
      case "marketing":     return <PageMarketing tenants={tenants} {...props}/>;
      case "agentops":      return <PageAgentOps {...props}/>;
      case "aisuite":       return <PageAISuite models={models} {...props}/>;
      case "safety":        return <SafetyPage cameras={cameras} events={events} {...props}/>;
      case "mqttagents":     return <MqttAgentControlPage token={localStorage.getItem("auravex_token")} />;
      case "aicommand":     return <AICommandPage {...props}/>;
      case "annotate":      return <PageAnnotate {...props}/>;
      case "platformconfig":return <PagePlatformConfig {...props}/>;
      case "modelrequests": return currentRole==="superadmin"?<PageModelRequests {...props}/>:<PageMyModelRequests {...props}/>;
      case "myrequests":    return <PageMyModelRequests {...props}/>;
      case "notifyrecipients":return <PageNotifyRecipients {...props}/>;
      case "training":      return <PageTraining models={models} {...props}/>;

      default:              return <PageDashboard  events={events} tenants={tenants} cameras={cameras} violations={violations} plans={plans}/>;
    }
  };

  /* -- RENDER --------------------------------------------------- */
  if(!loggedIn) return <LoginPage onLogin={handleLogin}/>;

  return(
    <div style={{display:"flex",height:"100vh",background:T.bg,fontFamily:T.font,color:T.text,overflow:"hidden"}}>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&family=IBM+Plex+Mono:wght@400;600;700&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
        html,body { background:#F0F2F7!important; }
        @keyframes slideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes micPulse {
          0%   { box-shadow: 0 0 0 0px rgba(239,68,68,.55); }
          50%  { box-shadow: 0 0 0 12px rgba(239,68,68,.0); }
          100% { box-shadow: 0 0 0 0px rgba(239,68,68,.0); }
        }
        @keyframes waveBar {
          0%,100% { height: 4px; opacity: .5; }
          50%     { height: 18px; opacity: 1; }
        }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:.3} }
        @keyframes scanLine {
          0%   { top: 0%; opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes flashOut {
          0%   { opacity: .9; }
          100% { opacity: 0; }
        }
        @keyframes aiPulse { 0%,100%{opacity:.4;transform:scale(.85)} 50%{opacity:1;transform:scale(1)} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:.3} }
        @keyframes scanLine {
          0%   { top: 0%; opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes flashOut {
          0%   { opacity: .9; }
          100% { opacity: 0; }
        }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background:#F0F2F7; }
        ::-webkit-scrollbar-thumb { background:${T.border}; border-radius:3px; }
        ::-webkit-scrollbar-thumb:hover { background:${T.hi}; }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:.3} }
        @keyframes scanLine {
          0%   { top: 0%; opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes flashOut {
          0%   { opacity: .9; }
          100% { opacity: 0; }
        }
        @keyframes aiPulse { 0%,100%{opacity:.25;transform:scale(.85)} 50%{opacity:1;transform:scale(1)} }
        @keyframes slideIn { from{transform:translateX(60px);opacity:0} to{transform:translateX(0);opacity:1} }
      `}</style>

      {/* Sidebar -- filtered by role */}
      <Sidebar active={active} setActive={setActive} events={events} tenants={tenants} cameras={cameras} currentRole={currentRole} pageAccess={pageAccess}/>

      {/* Main */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <TopBar active={active} events={events} time={time} currentRole={currentRole} setCurrentRole={setCurrentRole} loginUser={loginUser} onLogout={handleLogout}/>

        {/* Page content */}
        <div style={{flex:1,overflowY:"auto",padding:"16px 20px",background:T.bg}}>
          {renderPage()}
          <div style={{height:24}}/>
        </div>
      </div>

      {/* Toast */}
      {toast&&<Toast msg={toast.msg} type={toast.type}/>}

      {/* Floating AI Avatar -- available on all pages for all roles */}
      <FloatingAIAvatar currentRole={currentRole}/>
    </div>
  );
}
